import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { invalid } from '../validation.ts';
import {
  domError,
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

interface MediaCapabilitiesLike {
  decodingInfo(configuration: Record<string, unknown>): Promise<{
    supported: boolean;
    smooth: boolean;
    powerEfficient: boolean;
  }>;
}

interface NavigatorWithMediaCapabilities {
  readonly mediaCapabilities?: MediaCapabilitiesLike;
}

const codecSamples: Readonly<Record<string, string>> = {
  h264: 'video/mp4; codecs="avc1.42E01E"',
  aac: 'audio/mp4; codecs="mp4a.40.2"',
  vp8: 'video/webm; codecs="vp8"',
  vp9: 'video/webm; codecs="vp09.00.10.08"',
  av1: 'video/mp4; codecs="av01.0.04M.08"',
};

async function collect(): Promise<RawData> {
  const video = document.createElement('video');
  const canPlayType = Object.fromEntries(
    Object.entries(codecSamples).map(([name, contentType]) => [
      name,
      video.canPlayType(contentType),
    ]),
  );
  const mediaSource = Object.fromEntries(
    Object.entries(codecSamples).map(([name, contentType]) => [
      name,
      typeof MediaSource != 'undefined' &&
        MediaSource.isTypeSupported(contentType),
    ]),
  );
  const capabilities = (navigator as unknown as NavigatorWithMediaCapabilities)
    .mediaCapabilities;
  const mediaCapabilities: Record<string, unknown> = {};
  if (capabilities) {
    for (const [name, contentType] of Object.entries(codecSamples)) {
      if (!name.match(/h264|vp8|vp9|av1/)) {
        continue;
      }
      try {
        mediaCapabilities[name] = await capabilities.decodingInfo({
          type: 'file',
          video: {
            contentType,
            width: 1920,
            height: 1080,
            bitrate: 5_000_000,
            framerate: 30,
          },
        });
      } catch (error) {
        mediaCapabilities[name] = { error: domError(error) };
      }
    }
  }
  let widevine: RawData | null = null;
  if (navigator.requestMediaKeySystemAccess) {
    try {
      const access = await navigator.requestMediaKeySystemAccess(
        'com.widevine.alpha',
        [
          {
            initDataTypes: ['cenc'],
            videoCapabilities: [
              {
                contentType: codecSamples.h264,
                robustness: 'SW_SECURE_DECODE',
              },
            ],
          },
        ],
      );
      widevine = {
        keySystem: access.keySystem,
        configuration: access.getConfiguration(),
      };
    } catch (error) {
      widevine = {
        errorName: domError(error).name,
        errorMessage: domError(error).message,
      };
    }
  }
  return {
    canPlayType,
    mediaSource,
    mediaCapabilities,
    widevine,
  };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const widevine = isRecord(rawData.widevine) && rawData.widevine.keySystem;
  return widevine ? 'Widevine · codecs' : 'No Widevine · codecs';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const widevine = isRecord(rawData.widevine) ? rawData.widevine : {};
  return typeof widevine.keySystem == 'string'
    ? validation([])
    : invalid('Widevine is unavailable in Google Chrome configuration');
}

export const mediaCodecsDrmTest: TestDefinition = {
  id: 'mediaCodecsDrm',
  title: 'Media codecs / DRM',
  category: 'shared',
  format,
  run,
  validate,
};
